import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const legalContent = {
  'mentions-legales': {
    title: 'Mentions Légales',
    content: `
## Éditeur du site

**VIVANT**
Société organisatrice de retraites et programmes de développement pour entrepreneurs.

**Siège social :** 
À définir

**Contact :**
Email : contact@vivant.co
Téléphone : À définir

## Directeur de la publication

Philippe Alexis & Sabril Jurczeszyn, Co-fondateurs

## Hébergement

Le site est hébergé par :
Emergent Agent
https://emergentagent.com

## Propriété intellectuelle

L'ensemble de ce site relève de la législation française et internationale sur le droit d'auteur et la propriété intellectuelle. Tous les droits de reproduction sont réservés, y compris pour les documents téléchargeables et les représentations iconographiques et photographiques.

La reproduction de tout ou partie de ce site sur un support électronique quel qu'il soit est formellement interdite sauf autorisation expresse du directeur de la publication.

## Données personnelles

Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez d'un droit d'accès, de rectification et de suppression des données vous concernant. Pour exercer ce droit, contactez-nous à : contact@vivant.co

## Cookies

Ce site utilise des cookies pour améliorer l'expérience utilisateur et mesurer l'audience. En poursuivant votre navigation, vous acceptez l'utilisation de ces cookies.

## Crédits

Design & Développement : Emergent Agent
Photographies : VIVANT, droits réservés
    `
  },
  'cgv': {
    title: 'Conditions Générales de Vente',
    content: `
## Article 1 – Objet

Les présentes Conditions Générales de Vente (CGV) régissent les relations contractuelles entre VIVANT et tout participant inscrit aux programmes de retraite.

## Article 2 – Services proposés

VIVANT propose des retraites de 6 nuits destinées aux entrepreneurs, incluant :
- Hébergement en chambre individuelle
- Pension complète
- Programme d'activités (sport, business, bien-être)
- Accompagnement et coaching
- Transferts aéroport

## Article 3 – Prix

Le tarif de la retraite est de **6 500€** TTC, hors billets d'avion.

Ce tarif comprend :
- 6 nuits en chambre individuelle avec salle de bain privée
- Tous les repas (chef privé)
- Ensemble des activités et ateliers
- Coaching et formations business
- Hammam et massage
- Shooting photo professionnel
- Transferts aéroport
- Kit de bienvenue
- Suivi post-camp

## Article 4 – Inscription et paiement

L'inscription se fait via le formulaire de candidature en ligne. Suite à l'acceptation de votre candidature, un acompte de 30% (1 950€) sera demandé pour confirmer votre place. Le solde devra être réglé 30 jours avant le début de la retraite.

Moyens de paiement acceptés : virement bancaire, carte bancaire.

## Article 5 – Annulation par le participant

- **Plus de 60 jours avant** : remboursement intégral moins 200€ de frais de dossier
- **Entre 30 et 60 jours** : remboursement de 50%
- **Moins de 30 jours** : aucun remboursement

En cas de force majeure dûment justifiée, un avoir pourra être proposé pour une prochaine session.

## Article 6 – Annulation par VIVANT

En cas d'annulation de notre fait (nombre insuffisant de participants, force majeure), nous procéderons au remboursement intégral des sommes versées.

## Article 7 – Conditions de participation

Le participant déclare :
- Être majeur
- Être en bonne santé physique et mentale
- Avoir souscrit une assurance voyage et rapatriement
- Accepter les règles de vie en communauté pendant la retraite

## Article 8 – Responsabilité

VIVANT s'engage à mettre en œuvre tous les moyens nécessaires au bon déroulement de la retraite. Notre responsabilité ne saurait être engagée en cas de :
- Non-respect des consignes par le participant
- Cas de force majeure
- Fait d'un tiers

## Article 9 – Droit à l'image

Le participant autorise VIVANT à utiliser son image (photos, vidéos) à des fins de communication, sauf opposition expresse signifiée par écrit.

## Article 10 – Confidentialité

Les échanges et témoignages partagés pendant la retraite sont confidentiels. Chaque participant s'engage à respecter cette règle.

## Article 11 – Droit applicable et litiges

Les présentes CGV sont soumises au droit français. En cas de litige, les parties s'engagent à rechercher une solution amiable avant toute action judiciaire.

## Article 12 – Acceptation

L'inscription à une retraite VIVANT implique l'acceptation pleine et entière des présentes CGV.

---

*Dernière mise à jour : Décembre 2024*
    `
  },
  'confidentialite': {
    title: 'Politique de Confidentialité',
    content: `
## Introduction

VIVANT s'engage à protéger la vie privée des utilisateurs de son site et des participants à ses programmes. Cette politique de confidentialité explique comment nous collectons, utilisons et protégeons vos données personnelles.

## Responsable du traitement

**VIVANT**
Email : contact@vivant.co

## Données collectées

Nous collectons les données suivantes :

### Via le formulaire de candidature :
- Prénom, nom
- Adresse email
- Numéro de téléphone
- Profil professionnel (entrepreneur, dirigeant, freelance, etc.)
- Chiffre d'affaires approximatif
- Objectifs de participation
- Niveau d'engagement

### Via la navigation :
- Données de connexion (adresse IP, navigateur)
- Cookies de session et d'analyse

## Finalités du traitement

Vos données sont utilisées pour :
- Traiter votre candidature
- Vous contacter concernant votre inscription
- Vous envoyer des informations sur nos programmes (avec votre consentement)
- Améliorer notre site et nos services
- Établir des statistiques anonymes

## Base légale

Le traitement de vos données repose sur :
- Votre consentement (formulaire de candidature, newsletter)
- L'exécution du contrat (inscription à une retraite)
- Notre intérêt légitime (amélioration des services)

## Destinataires des données

Vos données sont accessibles uniquement :
- À l'équipe VIVANT
- À nos prestataires techniques (hébergement, emailing)

Nous ne vendons ni ne louons vos données à des tiers.

## Durée de conservation

- Candidatures non retenues : 1 an
- Participants : durée de la relation commerciale + 3 ans
- Données de navigation : 13 mois

## Vos droits

Conformément au RGPD, vous disposez des droits suivants :
- **Accès** : obtenir une copie de vos données
- **Rectification** : corriger des données inexactes
- **Effacement** : supprimer vos données
- **Opposition** : vous opposer à certains traitements
- **Portabilité** : recevoir vos données dans un format structuré
- **Limitation** : limiter le traitement de vos données

Pour exercer ces droits, contactez-nous à : contact@vivant.co

Vous pouvez également introduire une réclamation auprès de la CNIL.

## Sécurité

Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données contre l'accès non autorisé, la modification, la divulgation ou la destruction.

## Cookies

Notre site utilise des cookies pour :
- Assurer le bon fonctionnement du site
- Mesurer l'audience (anonymement)
- Améliorer votre expérience

Vous pouvez paramétrer vos préférences de cookies via votre navigateur.

## Modifications

Cette politique peut être mise à jour. Nous vous informerons de tout changement significatif.

## Contact

Pour toute question relative à cette politique :
Email : contact@vivant.co

---

*Dernière mise à jour : Décembre 2024*
    `
  }
};

const LegalPage = () => {
  const { page } = useParams();
  const content = legalContent[page];

  if (!content) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-italiana text-3xl text-white mb-4">Page non trouvée</h1>
          <Link to="/" className="text-[#D4A574] hover:underline">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <nav className="px-6 md:px-12 py-6 border-b border-white/10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-italiana text-xl md:text-2xl tracking-[0.4em] text-white">
            <span className="text-[#D4A574]">V</span>IVANT
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-white/60 hover:text-white text-sm transition-colors"
          >
            <ArrowLeft size={16} />
            Retour
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="px-6 md:px-12 py-12 md:py-20">
        <div className="max-w-4xl mx-auto">
          <h1 className="font-italiana text-3xl md:text-4xl lg:text-5xl tracking-[0.1em] text-white mb-12 text-center">
            {content.title}
          </h1>
          
          <div className="prose prose-invert prose-lg max-w-none">
            <div 
              className="text-white/70 leading-relaxed space-y-6"
              dangerouslySetInnerHTML={{ 
                __html: content.content
                  .replace(/^## (.+)$/gm, '<h2 class="font-italiana text-2xl text-white mt-10 mb-4 tracking-wider">$1</h2>')
                  .replace(/^### (.+)$/gm, '<h3 class="text-lg text-[#D4A574] mt-6 mb-2">$1</h3>')
                  .replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>')
                  .replace(/^- (.+)$/gm, '<li class="ml-4 text-white/60">$1</li>')
                  .replace(/\n\n/g, '</p><p class="mb-4">')
                  .replace(/---/g, '<hr class="border-white/10 my-8" />')
                  .replace(/\*(.+?)\*/g, '<em>$1</em>')
              }}
            />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="px-6 md:px-12 py-8 border-t border-white/10">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="font-italiana text-lg tracking-[0.3em] text-white/30">
            <span className="text-[#D4A574]/50">V</span>IVANT
          </span>
          <div className="flex gap-6 text-xs text-white/40">
            <Link to="/legal/mentions-legales" className="hover:text-white transition-colors">Mentions légales</Link>
            <Link to="/legal/cgv" className="hover:text-white transition-colors">CGV</Link>
            <Link to="/legal/confidentialite" className="hover:text-white transition-colors">Confidentialité</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LegalPage;
